import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuChevronDown, LuGlobe } from "react-icons/lu";

const LANGUAGES = [
  { code: "ar-TN", label: "تونسي", flag: "/flag-tn.svg" },
  { code: "ar", label: "العربية", flag: "/flag-sa.svg" },
  { code: "fr", label: "Français", flag: "/flag-fr.svg" },
  { code: "en", label: "English", flag: "/flag-gb.svg" },
];

// variant="row": mobile language row under the header (globe, language name, chevron); default: flag-only icon button.
export default function LanguageSelector({ variant } = {}) {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [changing, setChanging] = useState(false);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const resolved = i18n.resolvedLanguage || i18n.language || "fr";
  const code = resolved === 'ar-TN' ? resolved : resolved.split('-')[0];
  const current = LANGUAGES.find(language => language.code === code) || LANGUAGES.find(language => language.code === 'fr');

  useEffect(() => {
    const outside = event => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    };
    const escape = event => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [isOpen]);

  async function changeLanguage(language) {
    if (changing) return;
    setChanging(true);
    try {
      await i18n.changeLanguage(language.code);
        try { localStorage.setItem("language", language.code); } catch { /* Keep the live choice when storage is unavailable. */ }
      setIsOpen(false);
      buttonRef.current?.focus();
    } finally {
      setChanging(false);
    }
  }

  return (
    <div ref={containerRef} className="relative lina-language-control">
      <button ref={buttonRef} type="button" aria-haspopup="menu"
        aria-expanded={isOpen} aria-label={current.label} title={current.label}
        aria-busy={changing} disabled={changing} onClick={() => setIsOpen(value => !value)}
        className={variant === "row"
          ? "lina-m-langrow disabled:opacity-60"
          : variant === "bar"
            ? "lina-topbar-link disabled:opacity-60"
            : "inline-flex h-10 w-10 items-center justify-center rounded-xl text-2xl transition hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500/20 disabled:opacity-60"}>
        {variant === "row" || variant === "bar"
          ? <><LuGlobe aria-hidden="true" size={18} /><span lang={current.code} dir="auto">{current.label}</span><LuChevronDown aria-hidden="true" size={15} /></>
          : <span aria-hidden="true"><img src={current.flag} alt="" width="28" height="28" className="h-7 w-7 object-contain" /></span>}
      </button>
      {isOpen && <div role="menu" aria-label={t("ui.choose_a_language")}
        className="absolute end-0 top-full z-[90] mt-2 flex flex-col gap-1 rounded-xl border border-slate-100 bg-white p-1 shadow-xl">
        {LANGUAGES.map(language => <button key={language.code} type="button"
          role="menuitemradio" aria-checked={language.code === current.code}
          aria-label={language.label} title={language.label} disabled={changing}
          onClick={() => changeLanguage(language)}
          className={`inline-flex h-10 min-w-36 items-center gap-3 whitespace-nowrap rounded-lg px-3 text-sm transition focus:outline-none focus:ring-2 focus:ring-green-500/20 disabled:opacity-60 ${language.code === current.code ? "bg-green-50" : "hover:bg-slate-50"}`}>
          <span aria-hidden="true"><img src={language.flag} alt="" width="28" height="28" className="h-7 w-7 object-contain" /></span>
          <span lang={language.code} dir="auto">{language.label}</span>
        </button>)}
      </div>}
    </div>
  );
}
